import { CalendarClock, MapPin, Phone, Users } from 'lucide-react';
import { Reservation } from '../../../../types/restaurant/RestaurantReservationTypes';
import ReservationActions from './ReservationActions';
import ReservationStatusBadge from './ReservationStatusBadge';

interface Props {
  reservation: Reservation;
  arrivalWindowOpen: boolean;
  onEdit: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onNoShow: () => void;
  onOpenOrder: () => void;
}

const dateTime = (value: string) =>
  new Intl.DateTimeFormat('es-SV', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const ReservationCard = ({
  reservation,
  arrivalWindowOpen,
  onEdit,
  onConfirm,
  onCancel,
  onNoShow,
  onOpenOrder,
}: Props) => (
  <article className="rounded-xl border border-stroke bg-white p-5 shadow-card transition hover:border-primary/30 dark:border-strokedark dark:bg-boxdark">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          {reservation.reservationNumber}
        </p>
        <h2 className="mt-1 text-lg font-semibold text-black dark:text-white">
          {reservation.customerName ||
            reservation.guestName ||
            'Cliente sin nombre'}
        </h2>
      </div>
      <div className="flex flex-col items-end gap-2">
        <ReservationStatusBadge status={reservation.status} />
        <span
          className={`text-xs font-semibold ${
            reservation.active ? 'text-success' : 'text-bodydark2'
          }`}
        >
          {reservation.active ? 'Activa' : 'Histórica'}
        </span>
      </div>
    </div>
    <div className="mt-4 grid gap-2 text-sm text-bodydark2 sm:grid-cols-2">
      <span className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-primary" />
        {dateTime(reservation.startsAt)}
      </span>
      <span className="flex items-center gap-2">
        <Users className="h-4 w-4 text-primary" />
        {reservation.partySize} personas
      </span>
      <span className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary" />
        {reservation.tables.map((table) => table.code).join(', ') || 'Sin mesa'}
      </span>
      {reservation.guestPhone && (
        <span className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-primary" />
          {reservation.guestPhone}
        </span>
      )}
    </div>
    {reservation.notes && (
      <p className="mt-4 rounded-lg bg-gray-2 p-3 text-sm text-body dark:bg-boxdark-2 dark:text-bodydark">
        {reservation.notes}
      </p>
    )}
    <div className="mt-5 border-t border-stroke pt-4 dark:border-strokedark">
      <ReservationActions
        reservation={reservation}
        arrivalWindowOpen={arrivalWindowOpen}
        onEdit={onEdit}
        onConfirm={onConfirm}
        onCancel={onCancel}
        onNoShow={onNoShow}
        onOpenOrder={onOpenOrder}
      />
    </div>
  </article>
);

export default ReservationCard;
